import React, { useCallback, useEffect, useState } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import {
  getMatterRates,
  getNewEmail,
  getSuggestedEmails,
  shareDocumentOutlookWebURL,
} from "api";
import { useApplicationSettings } from "services/applicationSettingsService";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { CONSTANT } from "CONSTANT/Matter/MatterActivityForm";
import { Tooltip } from "@progress/kendo-react-tooltip";
import infoIcon from "../../../assets/CaseFileIcons/info-icon.png";
import { Label } from "@progress/kendo-react-labels";
import { Tile } from "components/UIComponents/Tile";
import { Loader } from "@progress/kendo-react-indicators";
import { toast } from "react-toastify";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { Input } from "@progress/kendo-react-inputs";

const SELECTED_FIELD = "selected";

const EmailSuggestedCheckboxCell = React.memo(function EmailSuggestedCheckboxCell({
  dataItem,
  field,
  onCellChange,
}) {
  const handleChange = (event) => {
    onCellChange({
      dataItem: { ...dataItem, [field]: event.target.checked },
      field,
      value: event.target.checked,
    });
  };

  return (
    <td className="">
      <input
        type="checkbox"
        style={{ width: "18px", height: "18px" }}
        className="cursor-pointer mt-1"
        checked={dataItem[field] || false}
        onChange={handleChange}
      />
    </td>
  );
});

const EmailSuggestedEmailCell = React.memo(function EmailSuggestedEmailCell({
  dataItem,
  onEmailChange,
}) {
  if (!dataItem?.isNew) {
    return <td className="fs-sm">{dataItem?.email ?? ""}</td>;
  }

  return (
    <td className="fs-sm">
      <Input
        value={dataItem.email ?? ""}
        onChange={(e) => onEmailChange(dataItem, e.target.value ?? "")}
      />
    </td>
  );
});

const SuggestedEmailsGrid = React.memo(function SuggestedEmailsGrid({
  data,
  setData,
}) {
  const handleCellChange = useCallback(
    (event) => {
      setData((prev) =>
        prev.map((email) =>
          email.id === event.dataItem.id
            ? { ...email, [event.field]: event.value }
            : email,
        ),
      );
    },
    [setData],
  );

  const handleEmailChange = useCallback(
    (dataItem, value) => {
      setData((prev) =>
        prev.map((item) => {
          if (item.id !== dataItem.id) return item;
          const selected = value?.trim() !== "";
          return { ...item, email: value, selected };
        }),
      );
    },
    [setData],
  );

  const renderCheckboxCell = useCallback(
    (props) => (
      <EmailSuggestedCheckboxCell {...props} onCellChange={handleCellChange} />
    ),
    [handleCellChange],
  );

  const renderEmailCell = useCallback(
    (props) => (
      <EmailSuggestedEmailCell
        dataItem={props.dataItem}
        onEmailChange={handleEmailChange}
      />
    ),
    [handleEmailChange],
  );

  return (
    <Grid data={data} dataItemKey="id" style={{ height: "320px" }} className="pb-1">
      <GridColumn
        field={SELECTED_FIELD}
        width="50px"
        title=" "
        cell={renderCheckboxCell}
      />
      <GridColumn
        field="name"
        title="Name"
        className="fs-sm fw-bold"
        width="180px"
      />
      <GridColumn
        field="email"
        title="Email"
        className="fs-sm"
        cell={renderEmailCell}
      />
      <GridColumn
        field="contactCategory"
        title="Category"
        className="fs-sm"
        width="130px"
      />
    </Grid>
  );
});

const EmailDialog = ({
  title,
  onClose,
  matterId,
  openExistingEmailDialog,
  matter,
  isShareDocumentMode = false,
  documentIds = [],
  shareType = 1,
}) => {
  const { data: applicationSettings } = useApplicationSettings();
  const [suggestedEmails, setSuggestedEmails] = useState([]);
  const [loading, setLoading] = useState(false); // State for loading
  const [inputState, setInputState] = useState({
    [CONSTANT.INPUT.UNITS]: 1,
    [CONSTANT.INPUT.MPU]: 6,
    [CONSTANT.INPUT.RATE]: 0.0,
    [CONSTANT.INPUT.VALUE]: 0.0,
  });

  useEffect(() => {
    const fetchSuggestedEmails = async () => {
      try {
        const emails = await getSuggestedEmails(matterId);

        const emailsWithId = emails.data?.data.map((email) => ({
          ...email,
          id: email.contactId, // Use contactId from API instead of random ID
          isNew: false,
        }));

        setSuggestedEmails([
          ...emailsWithId,
          {
            email: "",
            id: `new-${Date.now()}`, // Use timestamp for new email entry
            isNew: true,
          },
        ]);
      } catch (error) {
        console.error("Failed to fetch suggested emails", error);
      }
    };

    fetchSuggestedEmails();
  }, [matterId]);

  const openEmailClient = (type, subject, body, suggestedEmails) => {
    const toList = suggestedEmails
      .filter((email) => email.selected) // Get active emails
      .map((email) => email.email?.trim())
      .join(",");

    switch (type) {
      case "LinqApplication":
        openExistingEmailDialog({ to: toList, subject, body });
        break;

      case "OfficeWebMail":
        window.open(
          `https://outlook.office.com/mail/deeplink/compose?to=${toList}&subject=${encodeURIComponent(
            subject,
          )}&body=${encodeURIComponent(body)}`,
          "_blank",
          "noopener,noreferrer,width=800,height=800, left=100, top=100",
        );
        break;

      default:
        window.open(
          `mailto:${toList}?subject=${encodeURIComponent(
            subject,
          )}&body=${encodeURIComponent(body)}`,
          "_blank",
        );
        break;
    }

    onClose();
  };

  const openNewMail = async () => {
    setLoading(true);
    try {
      if (isShareDocumentMode) {
        const selectedEmails = suggestedEmails.filter((email) => email.selected);

        const toList = selectedEmails.map((email) => email.email?.trim()).join(",");

        const requestData = {
          toRecipients: toList.length > 0 ? toList.split(",") : [],
          body: " ",
          attachmentDocumentIDs: documentIds.filter((id) => id),
          shareType: shareType,
        };

        const response = await shareDocumentOutlookWebURL(matterId, requestData);
        const outlookUrl = response?.data?.data?.outlookWebUrl;

        if (outlookUrl) {
          window.open(
            outlookUrl,
            "_blank",
            "noopener,noreferrer,width=800,height=800,left=100,top=100",
          );
          onClose();
        } else {
          toast.error("Failed to get Outlook URL");
        }
      } else {
        const data = await getNewEmail(matterId, inputState[CONSTANT.INPUT.UNITS]);

        const defaultEmailClient = applicationSettings?.find(
          (x) => x.key === "DefaultEmailClient",
        )?.value;

        openEmailClient(
          defaultEmailClient,
          data?.data?.data?.subject,
          data?.data?.data?.body,
          suggestedEmails,
        );
      }
    } catch (error) {
      console.error("Failed to open email", error);
    } finally {
      setLoading(false);
    }
  };

  const [comboBoxState, setComboBoxState] = useState({
    [CONSTANT.COMBOBOX.TYPE]: "",
    [CONSTANT.COMBOBOX.FEE_EARNER]: "",
  });

  const [rateInfoText, setRateInfoText] = useState("");

  const getMatterRateValue = async () => {
    try {
      const data = await getMatterRates(matterId, matter?.feeEarnerRef);
      setInputState((prevState) => ({
        ...prevState,
        [CONSTANT.INPUT.RATE]: data?.data?.data?.rateValue,
      }));
      setRateInfoText(data?.data?.data?.rateInfo);
    } catch (error) {
      console.error("Error fetching matter rate value", error);
    }
  };

  useEffect(() => {
    if (!isShareDocumentMode && matter?.feeEarnerRef !== "") {
      getMatterRateValue();
    }
  }, [matter?.feeEarnerRef, isShareDocumentMode]);

  useEffect(() => {
    calculateValue();
  }, [
    inputState[CONSTANT.INPUT.RATE],
    inputState[CONSTANT.INPUT.UNITS],
    inputState[CONSTANT.INPUT.MPU],
  ]);

  const handleInputChange = (value, label) => {
    setInputState((prevState) => ({
      ...prevState,
      [label]: value,
    }));
  };

  const calculateValue = () => {
    if (
      inputState[CONSTANT.INPUT.RATE] &&
      inputState[CONSTANT.INPUT.UNITS] &&
      inputState[CONSTANT.INPUT.MPU]
    ) {
      const value =
        (inputState[CONSTANT.INPUT.RATE] / 60) *
        (inputState[CONSTANT.INPUT.UNITS] * inputState[CONSTANT.INPUT.MPU]);
      setInputState((prevState) => ({
        ...prevState,
        [CONSTANT.INPUT.VALUE]: value,
      }));
    }
  };

  const handleComboBoxChange = (e, label) => {
    setComboBoxState({
      ...comboBoxState,
      [label]: e.value,
    });
  };

  return (
    <Dialog title={title} onClose={onClose} width={"800px"}>
      <div className="dialog-content p-3 pb-1">
        <Tile className="p-3 ps-2 pe-2 py-2 pb-0 overflow-auto" height={"340px"}>
          <SuggestedEmailsGrid data={suggestedEmails} setData={setSuggestedEmails} />
        </Tile>
      </div>

      {loading && (
        <div
          className="loader-container d-flex justify-content-center align-items-center"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}>
          <Loader themeColor={"warning"} size={"large"} type={"pulsing"} />
        </div>
      )}

      <Tile
        heading="Time Record"
        headingClass=""
        className=" m-3 mt-1 p-3 ps-4 pe-4 pt-2 pb-0 overflow-auto"
        height={"160px"}>
        <div className="ps-2 d-flex align-items-center gap-3 ">
          <div className="d-flex flex-column right-align">
            <Label className="label mb-1">Units</Label>
            <NumericTextBox
              style={{ width: "186px", height: "32px" }}
              value={inputState[CONSTANT.INPUT.UNITS]}
              onChange={(e) => handleInputChange(e.value, CONSTANT.INPUT.UNITS)}
              spinners={true}
              format=""
            />
          </div>
          <div className="d-flex flex-column right-align">
            <div className="d-flex align-items-baseline ">
              <Label className="label mb-1">Rate</Label>
              <Tooltip anchorElement="target" position="top">
                <img
                  className="ms-1 cursor-pointer"
                  src={infoIcon}
                  title={rateInfoText}
                  style={{
                    height: "14px",
                    width: "14px",
                  }}
                />
              </Tooltip>
            </div>
            <NumericTextBox
              style={{ width: "186px", height: "32px" }}
              value={inputState[CONSTANT.INPUT.RATE]}
              onChange={(e) => handleInputChange(e.value, CONSTANT.INPUT.RATE)}
              spinners={false}
              format="n2"
              placeholder="0.00"
            />
          </div>
          <div className="d-flex flex-column right-align">
            <div className="d-flex align-items-baseline ">
              <Label className="label mb-1">Value</Label>
              <Tooltip anchorElement="target" position="top">
                <img
                  className="ms-1 cursor-pointer"
                  src={infoIcon}
                  title={`MPU = ${inputState[CONSTANT.INPUT.MPU]}`}
                  style={{
                    height: "14px",
                    width: "14px",
                  }}
                />
              </Tooltip>
            </div>
            <NumericTextBox
              style={{ width: "186px", height: "32px" }}
              value={inputState[CONSTANT.INPUT.VALUE]}
              onChange={(e) => handleInputChange(e.value, CONSTANT.INPUT.VALUE)}
              spinners={false}
              format="n2"
              placeholder="0.00"
            />
          </div>
        </div>
      </Tile>
      <div className="dialog-footer">
        <Button className="common-btn bgBtnSecondary me-2" onClick={onClose}>
          Close
        </Button>
        <Button className="common-btn  me-3" onClick={openNewMail}>
          {isShareDocumentMode ? "Share" : "Open Email"}
        </Button>
      </div>
    </Dialog>
  );
};

export default EmailDialog;
